import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useForm } from 'react-hook-form';

import { Lab, updateLab } from 'api/labs';
import queryClient from 'utils/queryClient';
import { labsQueryKey } from 'utils/queryHooks/useLabs';
import { LabFormFields, LabFormValues } from './LabFormFields';
import { normalizeLabNames, parseLabelSettings } from './formUtils';

interface EditFormProps {
  lab: Lab;
  onSuccess: () => void;
  onCancel: () => void;
  otherLabs: Lab[];
}

export const EditLabForm: React.FC<EditFormProps> = ({ lab, onSuccess, onCancel, otherLabs }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LabFormValues>({
    defaultValues: { labName: lab.name, labelSettings: '', isTestLab: Boolean(lab.isTestLab) },
    mode: 'onChange',
  });

  const updateMutation = useMutation(updateLab, {
    onSuccess: () => {
      queryClient.invalidateQueries(labsQueryKey);
      onSuccess();
    },
  });

  const labName = watch('labName');
  const labelSettings = watch('labelSettings');
  const isTestLab = watch('isTestLab');
  const normalizedOtherLabNames = normalizeLabNames(otherLabs.map((otherLab) => otherLab.name));
  const hasAtLeastOneField =
    labName.trim() !== lab.name || labelSettings.trim() !== '' || Boolean(isTestLab) !== Boolean(lab.isTestLab);
  const isSubmitting = updateMutation.isLoading;

  const onSubmit = (values: LabFormValues) => {
    updateMutation.reset();

    const parsedLabelSettings = parseLabelSettings(values.labelSettings);

    const data: { labName?: string; labelSettings?: Record<string, unknown>; isTestLab?: boolean } = {};
    if (values.labName.trim() !== lab.name) {
      data.labName = values.labName.trim();
    }
    if (parsedLabelSettings !== undefined) {
      data.labelSettings = parsedLabelSettings;
    }
    if (Boolean(values.isTestLab) !== Boolean(lab.isTestLab)) {
      data.isTestLab = Boolean(values.isTestLab);
    }

    updateMutation.mutate({ labId: lab.id, data });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      data-testid={`edit-lab-form-${lab.id}`}
      sx={{ mt: 1, maxWidth: 480 }}
    >
      <Grid container direction="column" spacing={2}>
        {updateMutation.isError && (
          <Grid item>
            <Alert severity="error" data-testid="update-lab-error-alert">
              Failed to update lab. Check the inputs and try again.
            </Alert>
          </Grid>
        )}
        <LabFormFields
          disabled={isSubmitting}
          errors={errors}
          existingLabNames={normalizedOtherLabNames}
          labelSettingsHelperText="Leave blank to keep the existing value"
          register={register}
          labNameInputTestId="edit-lab-name-input"
          labelSettingsInputTestId="edit-label-settings-input"
        />
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || !hasAtLeastOneField || Boolean(errors.labName)}
                data-testid="update-lab-submit-button"
                startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
              >
                {isSubmitting ? 'Saving…' : 'Save Changes'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCancel}
                disabled={isSubmitting}
                data-testid="update-lab-cancel-button"
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
